import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { ConfigProvider, Table, TableProps, PagingQuery } from 'tea-component';
import { DBTable } from '@home/components/TeaComponents';
import GameImage from '@home/components/GameImage';
import EllipsisHover from '@home/components/EllipsisHover';
import { getCountryFlag } from '@home/_config/countries';
import { isEmpty } from '@intelligence/v3/utils/common';
import { CommonColumnBuilders } from '@intelligence/v3/utils/columns';
// import { getHasRatioIcon, calcMetricColumnWidth } from '@intelligence/v3/utils/columns/utils';
import { multiRatioMetricSort } from '@intelligence/v3/utils/metric';
import { LANGUAGE_CN } from '@home/_config/config';
import { MAX_RATIO_DISPLAY_VALUE, formatKMBValue } from '../../utils';
import { PAGE_SIZE_OPTIONS } from '../../constants';

import type { ICasualCompetitorsDataSourceProps } from '../../types';

import './index.scss';

const { expandable } = Table.addons;

interface ITopPublisherRecord extends IRecord {
  companyId: string;
  companyName: string;
  companyLogo: string;
  companyCountry: string;
  companyCountryAbbre: string;
  companyGameCount: number;
  dau: number;
  dauRatio: number;
  dauPrev?: number;
  iapRevenue: number;
  iapRevenueRatio: number;
  iapRevenuePrev?: number;
  download: number;
  downloadRatio: number;
  downloadPrev?: number;
}

interface ITopPublishersTableProps extends IBaseProps, ICasualCompetitorsDataSourceProps {
  loading?: boolean;
  hasRevenue?: boolean;
  granularity?: 'monthly' | 'daily' | string;
  // 当前周期开始日期（用于环比气泡）
  startDate?: string;
  // 当前周期结束日期（用于环比气泡）
  endDate?: string;
  data?: ITopPublisherRecord[];
  showChecker?: boolean;
  bordered?: TableProps['bordered'];
  sorts?: IRatioMetricSortState<ITopPublisherRecord>[];
  onSortsChange?: (sorts: IRatioMetricSortState<ITopPublisherRecord>[]) => void;
  pagingQuery?: PagingQuery;
  onPagingChange?: (pagingQuery: PagingQuery) => void;
  checkedList?: string[];
  onCheckedListChange?: (checkedList: string[]) => void;
  kpiTipsI18nKeys?: {
    dau?: string;
    iap?: string;
    download?: string;
  };
  expandedKeys?: string[];
  onExpandedKeysChange?: (keys: string[]) => void;
  renderExpandedRow?: (record: ITopPublisherRecord) => React.ReactNode;
}

export const DEFAULT_PAGE_SIZE = 10;

const TopPublishersTable: React.FC<ITopPublishersTableProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    hasRevenue = true,
    dataSource,
    granularity = 'monthly',
    startDate,
    endDate,
    data = [],
    showChecker = false,
    bordered = 'all',
    sorts = [],
    onSortsChange,
    pagingQuery = { pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE },
    onPagingChange,
    checkedList = [],
    onCheckedListChange,
    kpiTipsI18nKeys,
    expandedKeys = [],
    onExpandedKeysChange,
    renderExpandedRow,
  } = props;

  const { locale, formatMessage } = useIntl();
  const teaLocale = locale === LANGUAGE_CN ? 'zh' : 'en';

  /**
   * 构建厂商行环比气泡数据（复用公共字段：实体类型、粒度、日期、厂商图标/名称）
   */
  const buildCompanyDataCompareData = (
    record: ITopPublisherRecord,
    value: number | undefined,
    prevValue: number | undefined,
    fallbackTitle: string,
    valueFormatter: (val?: number | string) => string,
  ) => ({
    entityType: 'company' as const,
    granularity: granularity as IGranularity,
    startDate,
    endDate,
    icon: record?.companyLogo,
    title: record?.companyName || fallbackTitle,
    value,
    prevValue,
    valueFormatter,
  });

  const sortedData = useMemo(() => {
    if (isEmpty(data)) {
      return [];
    }

    if (isEmpty(sorts)) {
      return [...data].sort((a, b) => {
        return (b?.dau ?? 0) - (a?.dau ?? 0);
      });
    }

    return multiRatioMetricSort([...data], sorts);
  }, [data, sorts]);
  const top5CompanyIds = (sortedData || []).slice(0, 5).map((item) => item?.companyId);

  const getMetricTitle = (metricKey: 'daily_avg_dau' | 'iap_revenue' | 'download') => {
    const isMonthly = granularity === 'monthly';
    const titleMap = {
      daily_avg_dau: isMonthly
        ? 'dbh_studio_casual_publisher_kpi_monthly_dau'
        : 'dbh_studio_casual_publisher_kpi_daily_dau',
      iap_revenue: isMonthly
        ? 'dbh_studio_casual_publisher_kpi_monthly_iap'
        : 'dbh_studio_casual_publisher_kpi_daily_iap',
      download: isMonthly
        ? 'dbh_studio_casual_publisher_kpi_monthly_download'
        : 'dbh_studio_casual_publisher_kpi_daily_download',
    };

    return formatMessage({ id: titleMap[metricKey] });
  };

  const renderCompanyInfo = (record: ITopPublisherRecord) => {
    const countryFlag = getCountryFlag(record?.companyCountryAbbre || '');

    return (
      <div className="dbh-top-publishers-table-company">
        <div className="dbh-top-publishers-table-company-avatar">
          <GameImage entityType="company" src={record?.companyLogo || ''} alt={record?.companyName || ''} />
        </div>
        <div className="dbh-top-publishers-table-company-content">
          <EllipsisHover className="dbh-top-publishers-table-company-name" text={record?.companyName || '--'} />
          <div className="dbh-top-publishers-table-company-country">
            {countryFlag && (
              <img
                className="dbh-top-publishers-table-company-country-flag"
                src={countryFlag}
                alt={record?.companyCountryAbbre || ''}
              />
            )}
            <EllipsisHover
              className="dbh-top-publishers-table-company-country-text"
              text={record?.companyCountry || '--'}
            />
          </div>
        </div>
      </div>
    );
  };

  const columns = useMemo(() => {
    const rankMap = new Map<string, number>();
    sortedData.forEach((item, index) => {
      const companyId = item?.companyId;
      if (companyId && !rankMap.has(companyId)) {
        rankMap.set(companyId, index);
      }
    });
    const dauMetricName = getMetricTitle('daily_avg_dau');
    const dauMetricTips = kpiTipsI18nKeys?.dau ? formatMessage({ id: kpiTipsI18nKeys.dau }) : undefined;
    // const dauWidth = calcMetricColumnWidth({
    //   title: dauMetricName,
    //   dataSource,
    //   hasSortIcon: true,
    //   hasRatioIcon: getHasRatioIcon(dataSource, 'daily_avg_dau', sorts),
    //   hasTipText: Boolean(dauMetricTips),
    // });

    const baseColumns = [
      ...(showChecker
        ? [
          CommonColumnBuilders.buildCheckerColumn({
            fixed: 'left' as const,
            width: 26,
            getRecordCheckOptionValue: (record: ITopPublisherRecord) => record?.companyId,
            values: top5CompanyIds,
            value: checkedList,
            onChange: onCheckedListChange,
            maxCheckLimit: 10,
            getIsAllChecked: (value?: string[], values?: string[] | null) => {
              if (!Array.isArray(values) || !Array.isArray(value)) {
                return false;
              }
              if (value.length !== values.length) {
                return false;
              }
              return values.every((item) => value.includes(item));
            },
          }),
        ]
        : []),
      CommonColumnBuilders.buildMedalSequenceColumn<ITopPublisherRecord>({
        fixed: 'left' as const,
        align: 'center',
        width: 40,
        getRank: (record) => {
          return rankMap.get(record?.companyId) ?? 0;
        },
      }),
      {
        key: 'company_info',
        header: formatMessage({ id: 'dbh_company' }),
        // width: 500,
        width: hasRevenue ? 350 : 560,
        fixed: 'left' as const,
        render: renderCompanyInfo,
      },
      CommonColumnBuilders.buildKMBMetricColumn<ITopPublisherRecord>({
        key: 'dau',
        locale,
        align: 'right',
        // width: dauWidth,
        width: 'auto',
        dataSource,
        metric: 'daily_avg_dau',
        metricName: dauMetricName,
        metricTips: dauMetricTips,
        sorts,
        onSortsChange,
        ratioValueConfigs: { maxNumber: MAX_RATIO_DISPLAY_VALUE },
        getValue: (record) => record?.dau,
        getRatioValue: (record) => record?.dauRatio,
        getDataCompareData: (record) =>
          buildCompanyDataCompareData(record, record?.dau, record?.dauPrev, dauMetricName, (val) =>
            formatKMBValue(val as number),
          ),
      }),
    ];

    if (hasRevenue) {
      const iapMetricName = getMetricTitle('iap_revenue');
      const iapMetricTips = kpiTipsI18nKeys?.iap ? formatMessage({ id: kpiTipsI18nKeys.iap }) : undefined;
      // const iapWidth = calcMetricColumnWidth({
      //   title: iapMetricName,
      //   dataSource,
      //   hasSortIcon: true,
      //   hasRatioIcon: getHasRatioIcon(dataSource, 'iap_revenue', sorts),
      //   hasTipText: Boolean(iapMetricTips),
      // });
      baseColumns.push(
        CommonColumnBuilders.buildKMBMetricColumn<ITopPublisherRecord>({
          key: 'iapRevenue',
          locale,
          align: 'right',
          // width: iapWidth,
          width: 'auto',
          dataSource,
          metric: 'iap_revenue',
          metricName: iapMetricName,
          metricTips: iapMetricTips,
          sorts,
          onSortsChange,
          ratioValueConfigs: { maxNumber: MAX_RATIO_DISPLAY_VALUE },
          getValue: (record) => record?.iapRevenue,
          getRatioValue: (record) => record?.iapRevenueRatio,
          getDataCompareData: (record) =>
            buildCompanyDataCompareData(
              record,
              record?.iapRevenue,
              record?.iapRevenuePrev,
              iapMetricName,
              (val) => `$${formatKMBValue(val as number)}`,
            ),
        }),
      );
    }

    const downloadMetricName = getMetricTitle('download');
    const downloadMetricTips = kpiTipsI18nKeys?.download ? formatMessage({ id: kpiTipsI18nKeys.download }) : undefined;
    // const downloadWidth = calcMetricColumnWidth({
    //   title: downloadMetricName,
    //   dataSource,
    //   hasSortIcon: true,
    //   hasRatioIcon: getHasRatioIcon(dataSource, 'download', sorts),
    //   hasTipText: Boolean(downloadMetricTips),
    // });
    baseColumns.push(
      CommonColumnBuilders.buildKMBMetricColumn<ITopPublisherRecord>({
        key: 'download',
        locale,
        align: 'right',
        // width: downloadWidth,
        width: 'auto',
        dataSource,
        metric: 'download',
        metricName: downloadMetricName,
        metricTips: downloadMetricTips,
        bubblePlacement: 'top-end',
        sorts,
        onSortsChange,
        ratioValueConfigs: { maxNumber: MAX_RATIO_DISPLAY_VALUE },
        getValue: (record) => record?.download,
        getRatioValue: (record) => record?.downloadRatio,
        getDataCompareData: (record) =>
          buildCompanyDataCompareData(record, record?.download, record?.downloadPrev, downloadMetricName, (val) =>
            formatKMBValue(val as number),
          ),
      }),
    );

    return baseColumns;
  }, [
    checkedList,
    formatMessage,
    granularity,
    startDate,
    endDate,
    hasRevenue,
    kpiTipsI18nKeys,
    locale,
    onCheckedListChange,
    onSortsChange,
    showChecker,
    sortedData,
    sorts,
    top5CompanyIds,
    dataSource,
  ]);

  const extraAddons = useMemo(() => {
    if (!renderExpandedRow) {
      return [];
    }
    return [
      expandable({
        expandedKeys,
        width: 36,
        onExpandedKeysChange: (keys: string[]) => {
          onExpandedKeysChange?.(keys);
        },
        shouldRecordExpandable: () => true,
        render: (record: ITopPublisherRecord) => {
          return renderExpandedRow(record);
        },
      }),
    ];
  }, [expandedKeys, onExpandedKeysChange, renderExpandedRow]);

  return (
    <div style={style} className={classnames('dbh-top-publishers-table', className, { 'has-revenue': hasRevenue })}>
      <ConfigProvider locale={teaLocale}>
        <DBTable
          className={classnames({
            'db-table--has-checker': showChecker,
          })}
          bordered={bordered}
          useHeaderSticky={false}
          usePagination={sortedData?.length > PAGE_SIZE_OPTIONS[0]}
          loading={loading}
          recordKey={(record: ITopPublisherRecord) => record?.companyId || ''}
          pagingQuery={pagingQuery}
          onPagingChange={onPagingChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          columns={columns}
          records={sortedData}
          extraAddons={extraAddons}
        />
      </ConfigProvider>
    </div>
  );
};

export default TopPublishersTable;
